/** @jsxImportSource @emotion/react */

import { Empty, Skeleton } from "antd";
import { TransferApi } from "api/transfer";
import {
  TransferPointHistoryItem,
  TransferPointHistoryPayload,
} from "api/transfer/type";
import PaginationTable from "components/Common/PaginationTable";
import { TimeLineCustom } from "components/Common/TimeLine";
import { TitleTable } from "components/Common/TitleTable";
import { TimeLineSelectCustom } from "components/Mobile/ui/TimeLine";
import { listCalendar } from "contants/list-calendar";
import { listHeaderTransferPointHistory } from "contants/list-header-table";
import { ResponseCode } from "contants/response";
import dayjs, { Dayjs } from "dayjs";
import { formatNumberWithCommas } from "helpers/formatNumber";
import { useCallback, useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";

import { TransferPageStyle } from "../style";
import { TransferPointHistoryStyle } from "./style";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_CALENDAR = listCalendar[2];

type State = {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  currentPage: number;
  idDate: string;
  visible: boolean;
  dataItem: TransferPointHistoryItem | undefined;
};

interface Props {
  username: string;
  refreshKey?: number;
}

const formatAmount = (value?: string) =>
  value ? formatNumberWithCommas(Number(value).toFixed(2)) : "-";

const TransferPointHistory = ({ username, refreshKey = 0 }: Props) => {
  const { t } = useTranslation(["transferPage", "transaction", "message"]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState<number>(0);
  const [data, setData] = useState<TransferPointHistoryItem[] | null>(null);
  const [state, setState] = useState<State>({
    startDate: DEFAULT_CALENDAR.startDate,
    endDate: DEFAULT_CALENDAR.endDate,
    currentPage: 1,
    idDate: DEFAULT_CALENDAR.id,
    visible: false,
    dataItem: undefined,
  });

  const isDisable =
    dayjs(state.startDate, "YYYY-MM-DD").isValid() &&
    dayjs(state.endDate, "YYYY-MM-DD").isValid();

  const fetchTransferPointHistory = useCallback(
    async ({
      page,
      startDate,
      endDate,
    }: {
      page: number;
      startDate: Dayjs | null;
      endDate: Dayjs | null;
    }) => {
      if (!username) {
        setData([]);
        setTotal(0);
        setCurrentPage(1);
        return;
      }

      try {
        setIsLoading(true);
        setError("");
        setData(null);

        const payload: TransferPointHistoryPayload = {
          username,
          page,
          size: DEFAULT_PAGE_SIZE,
          start: startDate?.format("YYYY-MM-DD").toString(),
          end: endDate?.format("YYYY-MM-DD").toString(),
        };

        const res = await TransferApi.transferPointHistory(username, payload);

        if (res.status === ResponseCode.SUCCESS) {
          setData(res.data || []);
          setTotal(res.totalCount || 0);
          setPageSize(res.pageSize || DEFAULT_PAGE_SIZE);
          setCurrentPage(res.currentPage || page);
          setState((prev) => ({
            ...prev,
            currentPage: res.currentPage || page,
          }));
          return;
        }

        setData([]);
        setTotal(0);
        setCurrentPage(page);
        setState((prev) => ({ ...prev, currentPage: page }));
        setError(res.msg || "");
      } catch (err) {
        setData([]);
        setTotal(0);
        setCurrentPage(page);
        setState((prev) => ({ ...prev, currentPage: page }));
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setIsLoading(false);
      }
    },
    [username]
  );

  useEffect(() => {
    fetchTransferPointHistory({
      page: 1,
      startDate: DEFAULT_CALENDAR.startDate,
      endDate: DEFAULT_CALENDAR.endDate,
    });
  }, [fetchTransferPointHistory, refreshKey]);

  const getData = () => {
    setCurrentPage(1);
    setState((prev) => ({ ...prev, currentPage: 1 }));
    fetchTransferPointHistory({
      page: 1,
      startDate: state.startDate,
      endDate: state.endDate,
    });
  };

  const handleOnChangeDate = (item: {
    id: string;
    label: string;
    startDate: Dayjs;
    endDate: Dayjs;
  }) => {
    setState((prev) => ({
      ...prev,
      currentPage: 1,
      endDate: item.endDate,
      startDate: item.startDate,
      idDate: item.id,
    }));
    setCurrentPage(1);
    fetchTransferPointHistory({
      page: 1,
      startDate: item.startDate,
      endDate: item.endDate,
    });
  };

  const handleSelectTime = (value: string) => {
    const itemSelect = listCalendar.find((itemFind) => itemFind.id === value);
    const nextStartDate = itemSelect?.startDate || null;
    const nextEndDate = itemSelect?.endDate || null;

    setState((prev) => ({
      ...prev,
      currentPage: 1,
      idDate: value,
      startDate: nextStartDate,
      endDate: nextEndDate,
    }));
    setCurrentPage(1);
    fetchTransferPointHistory({
      page: 1,
      startDate: nextStartDate,
      endDate: nextEndDate,
    });
  };

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
    setState((prev) => ({ ...prev, currentPage: page }));
    fetchTransferPointHistory({
      page,
      startDate: state.startDate,
      endDate: state.endDate,
    });
  };

  const renderLoading = () => (
    <div css={TransferPointHistoryStyle.listOrders}>
      {Array.from(Array(isMobile ? 3 : 5), (_, index) => (
        <Skeleton
          key={`transfer-point-history-loading-${index}`}
          title={false}
          css={
            isMobile
              ? TransferPointHistoryStyle.mobileLoading
              : TransferPointHistoryStyle.loading
          }
          active
        />
      ))}
    </div>
  );

  const renderEmpty = () => (
    <div css={TransferPointHistoryStyle.full}>
      <Empty description={error || t("message:Empty")} />
    </div>
  );

  const renderDesktopList = () => {
    if (!data?.length) {
      return renderEmpty();
    }

    return (
      <div css={TransferPointHistoryStyle.table}>
        {data.map((item, index) => (
          <div
            css={TransferPointHistoryStyle.tableContent}
            key={`${item.report_date}-${item.from}-${item.to}-${index}`}
          >
            <span>{item.from || "-"}</span>
            <span>{item.to || "-"}</span>
            <span>{item.report_date || "-"}</span>
            <span>{formatAmount(item.amount)}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderMobileList = () => {
    if (!data?.length) {
      return renderEmpty();
    }

    return (
      <div css={TransferPointHistoryStyle.cardList}>
        {data.map((item, index) => (
          <div
            css={TransferPointHistoryStyle.cardItem}
            key={`${item.report_date}-${item.from}-${item.to}-${index}`}
          >
            <div css={TransferPointHistoryStyle.cardRow}>
              <span css={TransferPointHistoryStyle.cardLabel}>
                {t("transaction:From")}
              </span>
              <span css={TransferPointHistoryStyle.cardValue}>
                {item.from || "-"}
              </span>
            </div>
            <div css={TransferPointHistoryStyle.cardRow}>
              <span css={TransferPointHistoryStyle.cardLabel}>
                {t("transaction:To")}
              </span>
              <span css={TransferPointHistoryStyle.cardValue}>
                {item.to || "-"}
              </span>
            </div>
            <div css={TransferPointHistoryStyle.cardRow}>
              <span css={TransferPointHistoryStyle.cardLabel}>
                {t("transaction:Report Date")}
              </span>
              <span css={TransferPointHistoryStyle.cardValue}>
                {item.report_date || "-"}
              </span>
            </div>
            <div css={TransferPointHistoryStyle.cardRow}>
              <span css={TransferPointHistoryStyle.cardLabel}>
                {t("transaction:Amount")}
              </span>
              <span
                css={[
                  TransferPointHistoryStyle.cardValue,
                  TransferPointHistoryStyle.amountValue,
                ]}
              >
                {formatAmount(item.amount)}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div css={TransferPageStyle.wrapInner}>
      <h3 className="title-heading">{t("TransferHistoryTitle")}</h3>

      {isMobile ? (
        <div css={TransferPointHistoryStyle.timeline}>
          <TimeLineSelectCustom
            endDate={state.endDate}
            getDataWager={getData}
            handleSelectTime={handleSelectTime}
            idDate={state.idDate}
            isDisable={isDisable}
            isLoading={isLoading}
            setState={setState}
            startDate={state.startDate}
            key="transfer-point-history-timeline-mobile"
          />
        </div>
      ) : (
        <TimeLineCustom
          endDate={state.endDate}
          getDataWager={getData}
          handleOnChangeDate={handleOnChangeDate}
          idDate={state.idDate}
          isDisable={isDisable}
          isLoading={isLoading}
          setState={setState}
          startDate={state.startDate}
          key="transfer-point-history-timeline"
        />
      )}

      {!isMobile && (
        <div css={TransferPointHistoryStyle.titleTable}>
          <TitleTable listTitle={listHeaderTransferPointHistory} />
        </div>
      )}
      {isLoading
        ? renderLoading()
        : isMobile
        ? renderMobileList()
        : renderDesktopList()}

      <PaginationTable
        lengthData={data?.length || 0}
        page={currentPage}
        pageSize={pageSize || DEFAULT_PAGE_SIZE}
        total={total}
        onChangePage={handleChangePage}
      />
    </div>
  );
};

export default TransferPointHistory;
